import { c as _c } from "react/compiler-runtime";
import { getRecipientDisplay, type RecipientDisplayMode, type RecipientInput } from "@intents-explorer/shared-api/transactions/recipient";
import { Short, getAddressLink } from "@/components/short";
type RecipientValueProps = RecipientInput & {
  /** Needed for explorer address links. */
  chain?: string | undefined;
  /** Needed for search-result links. */
  getLink?: (recipient: string) => string | undefined;
  /** Keeps truncation layout-specific. */
  length: number;
  /** Resets table scroll after navigation. */
  onInternalLinkClick?: (() => void) | undefined;
  /** Chooses recipient display for this UI surface. */
  recipientDisplayMode: RecipientDisplayMode;
  /** Controls Next link scroll restoration. */
  scroll?: boolean | undefined;
};
type RecipientDisplay = NonNullable<ReturnType<typeof getRecipientDisplay>>;
type RecipientDisplayValueProps = Omit<RecipientValueProps, keyof RecipientInput | "recipientDisplayMode"> & {
  /** Avoids recomputing the recipient display. */
  display: RecipientDisplay;
};
function RecipientDisplayValue(t0) {
  const $ = _c(12);
  const {
    display,
    chain,
    getLink,
    length,
    onInternalLinkClick,
    scroll
  } = t0;
  if (display.recipient === undefined) {
    let t1;
    if ($[0] !== display.text) {
      t1 = <span>{display.text}</span>;
      $[0] = display.text;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== chain || $[3] !== display.recipient || $[4] !== getLink) {
    t1 = getLink?.(display.recipient) ?? getAddressLink(chain, display.recipient);
    $[2] = chain;
    $[3] = display.recipient;
    $[4] = getLink;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== display.recipient || $[7] !== length || $[8] !== onInternalLinkClick || $[9] !== scroll || $[10] !== t1) {
    t2 = <Short text={display.recipient} length={length} link={t1} onInternalLinkClick={onInternalLinkClick} scroll={scroll} />;
    $[6] = display.recipient;
    $[7] = length;
    $[8] = onInternalLinkClick;
    $[9] = scroll;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
export function RecipientValue(t0) {
  const $ = _c(12);
  let props;
  let recipient;
  let recipientDisplayMode;
  let recipientType;
  if ($[0] !== t0) {
    ({
      recipientType,
      recipient,
      recipientDisplayMode,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = recipient;
    $[3] = recipientDisplayMode;
    $[4] = recipientType;
  } else {
    props = $[1];
    recipient = $[2];
    recipientDisplayMode = $[3];
    recipientType = $[4];
  }
  let t1;
  if ($[5] !== recipient || $[6] !== recipientDisplayMode || $[7] !== recipientType) {
    t1 = getRecipientDisplay({
      recipientType,
      recipient
    }, recipientDisplayMode);
    $[5] = recipient;
    $[6] = recipientDisplayMode;
    $[7] = recipientType;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const display = t1;
  if (display === undefined) {
    return null;
  }
  let t2;
  if ($[9] !== display || $[10] !== props) {
    t2 = <RecipientDisplayValue display={display} {...props} />;
    $[9] = display;
    $[10] = props;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
type RecipientLineProps = RecipientValueProps & {
  className?: string | undefined;
};
export function RecipientLine(t0) {
  const $ = _c(20);
  let className;
  let props;
  let recipient;
  let recipientDisplayMode;
  let recipientType;
  if ($[0] !== t0) {
    ({
      className,
      recipientType,
      recipient,
      recipientDisplayMode,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = recipient;
    $[4] = recipientDisplayMode;
    $[5] = recipientType;
  } else {
    className = $[1];
    props = $[2];
    recipient = $[3];
    recipientDisplayMode = $[4];
    recipientType = $[5];
  }
  let t1;
  if ($[6] !== recipient || $[7] !== recipientDisplayMode || $[8] !== recipientType) {
    t1 = getRecipientDisplay({
      recipientType,
      recipient
    }, recipientDisplayMode);
    $[6] = recipient;
    $[7] = recipientDisplayMode;
    $[8] = recipientType;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const display = t1;
  if (display === undefined) {
    let t2;
    if ($[10] !== className || $[11] !== recipientDisplayMode) {
      t2 = recipientDisplayMode === "table" ? <div className={className} /> : null;
      $[10] = className;
      $[11] = recipientDisplayMode;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    return t2;
  }
  let t2;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>Recipient:</span>;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== display || $[15] !== props) {
    t3 = <RecipientDisplayValue display={display} {...props} />;
    $[14] = display;
    $[15] = props;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== className || $[18] !== t3) {
    t4 = <div className={className}>{t2}{t3}</div>;
    $[17] = className;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
}